import { Fuel, Nullable, VehicleModelType } from '@orcar/common';
import {
  convertToKST,
  filterInsuranceAtom,
  filterOptionAtom,
  getHMFromNumber,
  getSureUserId,
  isMobileAtom,
  MODAL_TYPE,
  PAGE_NAME,
  setStaticDate,
  useGetVehicleModels,
  useRecoil,
} from '@orcar/common-d2c';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CSCenterImage from '@/assets/img/jejuro_cs_center.png';
import CarImage from '@/assets/img/jejuro_rent_info_car_image.png';
import JejuroSemiBanner from '@/assets/img/jejuro_semi_banner.jpg';
import StoryIcon from '@/assets/img/jeuro_story.png';
import { KakaoChannelButton } from '@/components/button/KakaoChannelButton';
import { KAKAOTALK_URL } from '@/constants/common';
import InFormationBanner from './components/InformationBanner';
import MobileSearchBox from './components/MobileSearchBox';
import RecommendSection from './components/recommendedCar/RecommendSection';
import DateTimeOption from './components/searchOption/DateTimeOption';
import SnsBanner from './components/SnsBanner';
import * as S from './styles';
import { MobileOptionSearchModal } from '../../components/modal/MobileOptionSearchModal';

const HomeLayout = () => {
  const navigate = useNavigate();
  const sureUserId = getSureUserId();

  const [isModalOpen, setIsModalOpen] = useState<Nullable<string>>(null);

  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const {
    recoilState: [recoilFilterOption, setRecoilFilterOption],
    resetRecoilState: resetFilterOption,
  } = useRecoil(filterOptionAtom);
  const { resetRecoilState: resetFilterInsurance } =
    useRecoil(filterInsuranceAtom);

  const [pickUpDate, setPickUpDate] = useState<Dayjs>(
    dayjs(setStaticDate('pickUp')),
  );
  const [dropOffDate, setDropOffDate] = useState<Dayjs>(
    dayjs(setStaticDate('dropOff')),
  );
  const [pickUpTimeValue, setPickUpTimeValue] = useState(800);
  const [dropOffTimeValue, setDropOffTimeValue] = useState(800);

  const pickTimeValue = getHMFromNumber(pickUpTimeValue);
  const dropTimeValue = getHMFromNumber(dropOffTimeValue);

  const pickUpAt = pickUpDate
    ?.set('hour', pickTimeValue.hh)
    .set('minute', pickTimeValue.mm);
  const dropOffAt = dropOffDate
    ?.set('hour', dropTimeValue.hh)
    .set('minute', dropTimeValue.mm);

  const { data: vehicleModelList } = useGetVehicleModels({
    params: {
      pickUpAt: convertToKST(setStaticDate('pickUp')),
      dropOffAt: convertToKST(setStaticDate('dropOff')),
      driverAge: undefined,
      numberOfPeople: undefined,
      drivingExperience: undefined,
      sureUserId: sureUserId ? Number(sureUserId) : undefined,
    },
  });

  const handleRecommendCar = (type: string) => {
    const pickUpAt = convertToKST(setStaticDate('pickUp'));
    const dropOffAt = convertToKST(setStaticDate('dropOff'));

    navigate({
      pathname: PAGE_NAME.search,
      search: `?pickUpAt=${pickUpAt}&dropOffAt=${dropOffAt}`,
    });

    if (Fuel.EV === type) {
      setRecoilFilterOption({
        ...recoilFilterOption,
        carFuelList: [Fuel.EV],
        carTypeList: [VehicleModelType.EV],
      });
    } else {
      setRecoilFilterOption({
        ...recoilFilterOption,
        carTypeList: [type],
      });
    }
  };

  const handleGetProducts = () => {
    if (!!pickUpAt && !!dropOffAt) {
      navigate({
        pathname: PAGE_NAME.search,
        search: new URLSearchParams({
          pickUpAt: convertToKST(pickUpAt),
          dropOffAt: convertToKST(dropOffAt),
        }).toString(),
      });
      if (isMobile) {
        setIsModalOpen(null);
      }
    }
  };

  useEffect(() => {
    if (location.pathname === '/') {
      resetFilterOption();
      resetFilterInsurance();
    }
  }, [location.pathname]);

  return (
    <S.Wrapper isMobile={isMobile}>
      <S.BackgroundImageWrapper isMobile={isMobile}>
        <img
          src={
            isMobile
              ? 'assets/img/jejuro_home_banner_mobile.jpg'
              : 'assets/img/jejuro_home_banner.jpg'
          }
          width={isMobile ? '100%' : 700}
          height={isMobile ? '200px' : 360}
          alt='제주로 홈 메인 이미지'
          style={{
            objectFit: 'cover',
            borderRadius: '8px',
          }}
        />
        {isMobile ? (
          <MobileSearchBox
            pickUpAt={pickUpAt}
            dropOffAt={dropOffAt}
            onRentalPeriodClick={() => setIsModalOpen(MODAL_TYPE.MOBILE_SEARCH)}
            onSearchButtonClick={handleGetProducts}
          />
        ) : (
          <DateTimeOption
            pickUpDate={pickUpDate}
            dropOffDate={dropOffDate}
            setPickUpDate={setPickUpDate}
            setDropOffDate={setDropOffDate}
            pickUpTimeValue={pickUpTimeValue}
            dropOffTimeValue={dropOffTimeValue}
            setPickUpTimeValue={setPickUpTimeValue}
            setDropOffTimeValue={setDropOffTimeValue}
            onClick={handleGetProducts}
          />
        )}
      </S.BackgroundImageWrapper>

      {!isMobile && (
        <SemiBannerWrapper>
          <img src={JejuroSemiBanner} width={'100%'} height={'175px'} />
        </SemiBannerWrapper>
      )}

      <RecommendSection
        vehicleModelList={vehicleModelList}
        onClick={handleRecommendCar}
        isMobile={isMobile}
      />

      {!isMobile && <SnsBanner iconImageUrl={StoryIcon} />}

      {!isMobile && (
        <InformationBannerWrapper>
          <InFormationBanner
            iconImageUrl={CarImage}
            title='렌터카 이용안내'
            description='예약 및 셔틀 정보 확인하기'
            ellipseColor='#009D8A'
            backgroundColor='#00C2B6'
            onClick={() => navigate(`${PAGE_NAME.rent_info}?type=reservation`)}
          />
          <InFormationBanner
            iconImageUrl={CSCenterImage}
            title='자주 묻는 질문'
            description='궁금한 것들 다 모아봤어요'
            ellipseColor='#00B972'
            backgroundColor='#00D3A7'
            onClick={() => navigate(`${PAGE_NAME.cs_center}?section=FAQ`)}
          />
        </InformationBannerWrapper>
      )}

      <MobileOptionSearchModal
        showModal={isModalOpen === MODAL_TYPE.MOBILE_SEARCH}
        onClose={() => setIsModalOpen(null)}
        pickUpDate={pickUpDate}
        setPickUpDate={setPickUpDate}
        dropOffDate={dropOffDate}
        setDropOffDate={setDropOffDate}
        pickUpTimeValue={pickUpTimeValue}
        setPickUpTimeValue={setPickUpTimeValue}
        dropOffTimeValue={dropOffTimeValue}
        setDropOffTimeValue={setDropOffTimeValue}
        handleNextBtn={handleGetProducts}
      />
      <KakaoChannelButton linkUrl={KAKAOTALK_URL} />
    </S.Wrapper>
  );
};

export default HomeLayout;

const SemiBannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
  width: 1200px;
`;

const InformationBannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  width: 100%;
  height: 186px;
  margin-top: 40px;
`;
